// Frontend JavaScript entry point
import './frontend.css';

/**
 * Good Days Calendar Manager - Frontend JavaScript
 */

(function() {
'use strict';

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFrontend);
} else {
    initFrontend();
}

function initFrontend() {
    // Main frontend object
    const GDCMFrontend = {
        calendar: null,
        modal: null,
        currentEvent: null,
        isProcessing: false, // Flag to prevent double submissions
        
        init: function() {
            this.initCalendar();
            this.bindEvents();
        },

        initCalendar: function() {
            const calendarEl = document.getElementById('gdcm-frontend-calendar');
            if (!calendarEl) return;

            // Skip if already initialized or if inside member dashboard
            if (calendarEl.hasAttribute('data-calendar-initialized') || 
                calendarEl.closest('#gdcm-member-dashboard')) {
                return;
            }

            // Mark as initialized to prevent conflicts
            calendarEl.setAttribute('data-calendar-initialized', 'true');

            // Always use custom calendar - no FullCalendar fallback
            if (!window.GDCMCalendar) {
                console.error('GDCMCalendar not available - check if custom-calendar.js is loaded');
                return;
            }

            this.calendar = new window.GDCMCalendar(calendarEl, {
                initialView: 'dayGridMonth',
                headerToolbar: {
                    left: 'prev,next,today',
                    center: 'title',
                    right: 'dayGridMonth,listWeek'
                },
                events: gdcmData.root + 'events',
                eventClick: (info) => {
                    this.showBookingModal(info.event);
                },
                eventDidMount: (info) => {
                    // Add custom classes and styling based on event type for new custom calendar
                    const eventType = info.event.extendedProps.type || 'class';
                    info.el.classList.add(`gdcm-event-${eventType}`);
                    
                    // Add tooltip or additional info
                    info.el.setAttribute('title', this.getEventTooltip(info.event));
                    
                    // Disable past events
                    if (info.event.start < new Date()) {
                        info.el.classList.add('gdcm-event-past');
                        info.el.style.opacity = '0.6';
                        info.el.style.pointerEvents = 'none';
                    }
                },
                isAdmin: false, // Frontend context
                dayMaxEvents: 3,
                moreLinkClick: 'popover',
                height: 'auto',
                aspectRatio: 1.6,
                firstDay: 1, // Monday
                businessHours: {
                    daysOfWeek: [1, 2, 3, 4, 5, 6], // Monday - Saturday
                    startTime: '09:00',
                    endTime: '18:00'
                },
                eventOrder: 'start',
                eventDisplay: 'block'
            });

            this.calendar.render();
            
            // Add loading animation
            calendarEl.classList.add('fade-in');
        },

        bindEvents: function() {
            // Modal events
            document.addEventListener('click', (e) => {
                if (e.target.matches('.gdcm-modal-close, .gdcm-modal')) {
                    if (e.target === e.currentTarget || e.target.matches('.gdcm-modal-close')) {
                        this.closeModal();
                    }
                }
            });

            // Form submission
            document.addEventListener('submit', (e) => {
                if (e.target.matches('#gdcm-book-form')) {
                    e.preventDefault();
                    this.handleBookingForm(e.target);
                }
            });

            // Form validation
            document.addEventListener('input', (e) => {
                if (e.target.matches('#child-age')) {
                    this.validateAge(e.target);
                }
                if (e.target.matches('#parent-email, #contact-email')) {
                    this.validateEmail(e.target);
                }
            });

            // Escape key to close modal
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.modal && !this.modal.classList.contains('hidden')) {
                    this.closeModal();
                }
            });

            // Filter events by type
            document.addEventListener('change', (e) => {
                if (e.target.matches('#event-type-filter')) {
                    this.filterEventsByType(e.target.value);
                }
            });
        },

        showBookingModal: function(event) {
            this.currentEvent = event;
            const eventType = event.extendedProps.type || 'class';
            const isPast = event.start < new Date();
            
            if (isPast) {
                this.showNotification('Cannot book past events', 'error');
                return;
            }

            // Get available time info for party slots
            const startTime = new Date(event.start);
            const endTime = new Date(event.end);
            const availableHours = Math.round((endTime - startTime) / (1000 * 60 * 60));

            const modalHtml = `
                <div class="gdcm-modal" id="gdcm-booking-modal">
                    <div class="gdcm-modal-content">
                        <div class="gdcm-modal-header">
                            <div>
                                <h3 class="gdcm-modal-title">${event.title}</h3>
                                <p class="gdcm-modal-subtitle">
                                    ${startTime.toLocaleDateString()}${eventType === 'party_slot' || eventType === 'party' ? 
                                        ` - Available ${startTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} to ${endTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}` : 
                                        ` at ${startTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`
                                    }
                                </p>
                            </div>
                            <button type="button" class="gdcm-modal-close" aria-label="Close modal">
                                ×
                            </button>
                        </div>
                        
                        <div class="gdcm-modal-body">
                            <div class="tw-mb-6">
                                <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
                                    <span class="gdcm-event-type gdcm-event-type-${eventType}">${eventType === 'party_slot' ? 'Party Slot' : eventType}</span>
                                    <span class="tw-text-sm tw-text-gray-600">
                                        ${eventType === 'party_slot' || eventType === 'party' ? `${availableHours} hours available` : `${event.extendedProps.duration || 60} minutes`}
                                    </span>
                                </div>
                                
                                ${event.extendedProps.description ? `
                                    <p class="tw-text-gray-700 tw-mb-4">${event.extendedProps.description}</p>
                                ` : ''}
                                
                                <div class="tw-grid tw-grid-cols-2 tw-gap-4 tw-text-sm tw-bg-gray-50 tw-p-4 tw-rounded-lg">
                                    ${eventType === 'class' ? `
                                        <div>
                                            <span class="tw-font-semibold tw-text-gray-600">Instructor:</span>
                                            <span class="tw-block">${event.extendedProps.instructor || 'TBA'}</span>
                                        </div>
                                        <div>
                                            <span class="tw-font-semibold tw-text-gray-600">Age Range:</span>
                                            <span class="tw-block">${this.getAgeRange(event.extendedProps)}</span>
                                        </div>
                                        <div>
                                            <span class="tw-font-semibold tw-text-gray-600">Price:</span>
                                            <span class="tw-block tw-font-bold tw-text-calendar-blue">$${event.extendedProps.price || '0'}</span>
                                        </div>
                                    ` : `
                                        <div>
                                            <span class="tw-font-semibold tw-text-gray-600">Available From:</span>
                                            <span class="tw-block">${startTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                        </div>
                                        <div>
                                            <span class="tw-font-semibold tw-text-gray-600">Available Until:</span>
                                            <span class="tw-block">${endTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                        </div>
                                        <div>
                                            <span class="tw-font-semibold tw-text-gray-600">Total Available:</span>
                                            <span class="tw-block tw-font-bold tw-text-green-600">${availableHours} hours</span>
                                        </div>
                                    `}
                                </div>
                            </div>

                            <form id="gdcm-book-form" class="tw-space-y-4">
                                <input type="hidden" id="gdcm-eid" name="event_id" value="${event.id}">
                                
                                ${eventType === 'party_slot' || eventType === 'party' ? `
                                    <!-- Party Time Selection -->
                                    <div class="tw-bg-blue-50 tw-p-4 tw-rounded-lg tw-mb-4">
                                        <h4 class="tw-font-semibold tw-mb-3 tw-text-blue-900">Select Your Party Time</h4>
                                        <div class="gdcm-form-row">
                                            <div class="gdcm-form-group">
                                                <label for="party-start-time" class="gdcm-label">Start Time *</label>
                                                <select id="party-start-time" name="party_start_time" class="gdcm-select" required>
                                                    ${this.generateTimeOptions(startTime, endTime)}
                                                </select>
                                            </div>
                                            <div class="gdcm-form-group">
                                                <label for="party-duration" class="gdcm-label">Duration (hours) *</label>
                                                <select id="party-duration" name="party_duration" class="gdcm-select" required>
                                                    <option value="1">1 hour</option>
                                                    <option value="2" selected>2 hours</option>
                                                    <option value="3">3 hours</option>
                                                    <option value="4">4 hours</option>
                                                    <option value="5">5 hours</option>
                                                    <option value="6">6 hours</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div class="tw-text-sm tw-text-blue-700 tw-mt-2">
                                            <span id="selected-time-info">Selected: 2 hours starting at ${startTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                        </div>
                                    </div>
                                    
                                    <!-- Party Contact Information -->
                                    <div class="gdcm-form-row">
                                        <div class="gdcm-form-group">
                                            <label for="contact-name" class="gdcm-label">Contact Name *</label>
                                            <input type="text" id="contact-name" name="contact_name" class="gdcm-input" required placeholder="Person booking the party">
                                        </div>
                                        <div class="gdcm-form-group">
                                            <label for="contact-email" class="gdcm-label">Email Address *</label>
                                            <input type="email" id="contact-email" name="contact_email" class="gdcm-input" required>
                                        </div>
                                    </div>

                                    <div class="gdcm-form-row">
                                        <div class="gdcm-form-group">
                                            <label for="contact-phone" class="gdcm-label">Phone Number *</label>
                                            <input type="tel" id="contact-phone" name="contact_phone" class="gdcm-input" required>
                                        </div>
                                        <div class="gdcm-form-group">
                                            <label for="party-size" class="gdcm-label">Expected Party Size *</label>
                                            <input type="number" id="party-size" name="party_size" class="gdcm-input" min="1" required placeholder="Number of guests">
                                        </div>
                                    </div>

                                    <div class="gdcm-form-group">
                                        <label for="party-type" class="gdcm-label">Type of Celebration</label>
                                        <select id="party-type" name="party_type" class="gdcm-select">
                                            <option value="birthday">Birthday Party</option>
                                            <option value="company">Company Event</option>
                                            <option value="family">Family Gathering</option>
                                            <option value="friends">Friends Celebration</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>

                                    <div class="gdcm-form-group">
                                        <label for="special-requests" class="gdcm-label">Special Requests or Notes</label>
                                        <textarea id="special-requests" name="special_requests" class="gdcm-textarea" rows="3" placeholder="Special decorations, dietary requirements, accessibility needs, etc."></textarea>
                                    </div>
                                ` : `
                                    <!-- Class Booking Form -->
                                    <div class="gdcm-form-row">
                                        <div class="gdcm-form-group">
                                            <label for="parent-name" class="gdcm-label">Parent/Guardian Name *</label>
                                            <input type="text" id="parent-name" name="parent_name" class="gdcm-input" required>
                                        </div>
                                        <div class="gdcm-form-group">
                                            <label for="parent-email" class="gdcm-label">Email Address *</label>
                                            <input type="email" id="parent-email" name="parent_email" class="gdcm-input" required>
                                        </div>
                                    </div>

                                    <div class="gdcm-form-row">
                                        <div class="gdcm-form-group">
                                            <label for="parent-phone" class="gdcm-label">Phone Number *</label>
                                            <input type="tel" id="parent-phone" name="parent_phone" class="gdcm-input" required>
                                        </div>
                                        <div class="gdcm-form-group">
                                            <label for="child-name" class="gdcm-label">Child's Name *</label>
                                            <input type="text" id="child-name" name="child_name" class="gdcm-input" required>
                                        </div>
                                    </div>

                                    <div class="gdcm-form-row">
                                        <div class="gdcm-form-group">
                                            <label for="child-age" class="gdcm-label">Child's Age *</label>
                                            <input type="number" id="child-age" name="child_age" class="gdcm-input" min="1" max="18" required>
                                            <div class="age-validation-message tw-hidden tw-text-sm tw-mt-1"></div>
                                        </div>
                                        <div class="gdcm-form-group">
                                            <label for="child-birthday" class="gdcm-label">Child's Birthday (optional)</label>
                                            <input type="text" id="child-birthday" name="child_birthday" class="gdcm-input" placeholder="e.g., March 15, 2020">
                                        </div>
                                    </div>

                                    <div class="gdcm-form-row">
                                        <div class="gdcm-form-group">
                                            <label for="num-children" class="gdcm-label">Number of Children</label>
                                            <select id="num-children" name="num_children" class="gdcm-select">
                                                <option value="1">1 child</option>
                                                <option value="2">2 children</option>
                                                <option value="3">3 children</option>
                                                <option value="4">4+ children</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div class="gdcm-form-group">
                                        <label for="special-requests" class="gdcm-label">Special Requests or Notes</label>
                                        <textarea id="special-requests" name="special_requests" class="gdcm-textarea" rows="3" placeholder="Any allergies, special needs, or additional information..."></textarea>
                                    </div>
                                `}

                                <div class="tw-bg-blue-50 tw-p-4 tw-rounded-lg tw-text-sm tw-text-blue-800">
                                    <h4 class="tw-font-semibold tw-mb-2">Booking Information:</h4>
                                    <ul class="tw-space-y-1">
                                        <li>• You will receive a confirmation email within 24 hours</li>
                                        <li>• Cancellations must be made at least 24 hours in advance</li>
                                        ${eventType === 'party_slot' || eventType === 'party' ? 
                                            '<li>• Once booked, your selected time slot will be reserved exclusively for your party</li>' :
                                            ''
                                        }
                                        ${gdcmData.payment && gdcmData.payment.enabled ? 
                                            '<li>• A booking deposit is required to confirm your reservation</li>' :
                                            '<li>• Payment can be made upon arrival or through our online portal</li>'
                                        }
                                    </ul>
                                </div>

                                <!-- Payment Section (will be populated if payments are enabled) -->
                                <div id="gdcm-payment-container"></div>

                                <div class="tw-flex tw-justify-end tw-space-x-3 tw-pt-6 tw-border-t tw-border-gray-200" id="gdcm-booking-actions">
                                    <button type="button" class="gdcm-button-secondary gdcm-modal-close">Cancel</button>
                                    <button type="submit" class="gdcm-button" id="gdcm-submit-booking">
                                        <span class="button-text">${gdcmData.payment && gdcmData.payment.enabled ? 'Continue to Payment' : 'Book ' + (eventType === 'party_slot' || eventType === 'party' ? 'Party Slot' : 'Class')}</span>
                                        <span class="loading-text tw-hidden">Processing...</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            `;

            document.body.insertAdjacentHTML('beforeend', modalHtml);
            this.modal = document.getElementById('gdcm-booking-modal');
            this.modal.classList.remove('hidden');
            
            // Add event listeners for party time selection
            if (eventType === 'party_slot' || eventType === 'party') {
                this.bindPartyTimeSelectors();
            }
            
            // Focus first input
            setTimeout(() => {
                const firstInput = this.modal.querySelector('input[type="text"]');
                if (firstInput) firstInput.focus();
            }, 100);
        },

        generateTimeOptions: function(startTime, endTime) {
            const options = [];
            const current = new Date(startTime);
            const end = new Date(endTime);
            
            while (current < end) {
                const timeStr = current.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                const value = current.toTimeString().substr(0, 5); // HH:MM format
                options.push(`<option value="${value}">${timeStr}</option>`);
                current.setMinutes(current.getMinutes() + 30); // 30-minute intervals
            }
            
            return options.join('');
        },

        bindPartyTimeSelectors: function() {
            const startTimeSelect = this.modal.querySelector('#party-start-time');
            const durationSelect = this.modal.querySelector('#party-duration');
            const timeInfoSpan = this.modal.querySelector('#selected-time-info');
            
            if (!startTimeSelect || !durationSelect || !timeInfoSpan) return;
            
            const updateTimeInfo = () => {
                const startTime = startTimeSelect.value;
                const duration = parseInt(durationSelect.value);
                
                if (startTime && duration) {
                    // Calculate end time
                    const [hours, minutes] = startTime.split(':').map(Number);
                    const startDate = new Date();
                    startDate.setHours(hours, minutes, 0, 0);
                    
                    const endDate = new Date(startDate);
                    endDate.setHours(endDate.getHours() + duration);
                    
                    const startTimeStr = startDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                    const endTimeStr = endDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                    
                    timeInfoSpan.textContent = `Selected: ${duration} hour${duration > 1 ? 's' : ''} from ${startTimeStr} to ${endTimeStr}`;
                }
            };
            
            startTimeSelect.addEventListener('change', updateTimeInfo);
            durationSelect.addEventListener('change', updateTimeInfo);
            
            // Initial update
            updateTimeInfo();
        },

        handleBookingForm: function(form) {
            // Prevent double submissions
            if (this.isProcessing) {
                console.log('GDCM: Form submission already in progress, ignoring duplicate');
                return;
            }
            
            if (!this.validateForm(form)) {
                return;
            }

            // Set processing flag immediately
            this.isProcessing = true;

            const submitBtn = form.querySelector('button[type="submit"]');
            const buttonText = submitBtn.querySelector('.button-text');
            const loadingText = submitBtn.querySelector('.loading-text');
            
            // Show loading state and disable button
            buttonText.classList.add('tw-hidden');
            loadingText.classList.remove('tw-hidden');
            submitBtn.disabled = true;
            submitBtn.style.pointerEvents = 'none'; // Extra protection against clicks

            const formData = new FormData(form);
            const data = Object.fromEntries(formData.entries());
            const eventType = this.currentEvent?.extendedProps?.type || 'class';

            // Check if payments are enabled and this is the first step
            const paymentEnabled = gdcmData.payment && gdcmData.payment.enabled && gdcmData.payment.stripePublishableKey;
            const paymentContainer = document.getElementById('gdcm-payment-container');
            const isPaymentStep = paymentContainer && paymentContainer.innerHTML.includes('gdcm-payment-section');

            if (paymentEnabled && !isPaymentStep) {
                // First step: Show payment form
                this.initializePaymentForm(form, data, eventType, submitBtn, buttonText, loadingText);
                return;
            }

            if (paymentEnabled && isPaymentStep) {
                // Second step: Process payment
                this.processStripePayment(form, data, eventType, submitBtn, buttonText, loadingText);
                return;
            }

            // No payments - proceed with regular booking
            this.submitBookingWithoutPayment(data, eventType, submitBtn, buttonText, loadingText);
        },

        /**
         * Initialize the Stripe payment form
         */
        initializePaymentForm: async function(form, data, eventType, submitBtn, buttonText, loadingText) {
            try {
                // Calculate price
                const price = this.calculateBookingPrice(eventType, data);
                
                // Get customer info
                const customerEmail = data.parent_email || data.contact_email;
                const customerName = data.parent_name || data.contact_name;
                
                if (!window.GDCMStripePayment || !window.GDCMStripePayment.stripe) {
                    throw new Error('Payment system not available. Please refresh and try again.');
                }

                // Get the payment container
                const paymentContainer = document.getElementById('gdcm-payment-container');
                if (!paymentContainer) {
                    throw new Error('Payment container not found');
                }

                // Insert payment form HTML
                paymentContainer.innerHTML = window.GDCMStripePayment.getPaymentFormHTML(price);

                // Create payment intent and mount element
                const success = await window.GDCMStripePayment.createPaymentIntent({
                    amount: price,
                    bookingType: eventType === 'party_slot' || eventType === 'party' ? 'party' : 'class',
                    eventId: parseInt(data.event_id),
                    customerEmail: customerEmail,
                    customerName: customerName,
                    childName: data.child_name || '',
                    partySize: data.party_size ? parseInt(data.party_size) : 0,
                    containerElement: document.getElementById('gdcm-payment-element')
                });

                if (!success) {
                    throw new Error('Failed to initialize payment form');
                }

                // Update button text and re-enable
                buttonText.textContent = 'Pay & Confirm Booking';
                buttonText.classList.remove('tw-hidden');
                loadingText.classList.add('tw-hidden');
                submitBtn.disabled = false;
                submitBtn.style.pointerEvents = ''; // Re-enable clicks

                // Scroll payment form into view
                paymentContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
                
                // Reset processing flag - payment form is shown, user can now enter card details
                this.isProcessing = false;

            } catch (error) {
                console.error('Payment initialization error:', error);
                // Reset processing flag on error
                this.isProcessing = false;
                this.showNotification(error.message || 'Failed to initialize payment. Please try again.', 'error');
                buttonText.classList.remove('tw-hidden');
                loadingText.classList.add('tw-hidden');
                submitBtn.disabled = false;
                submitBtn.style.pointerEvents = ''; // Re-enable clicks
            }
        },

        /**
         * Process Stripe payment and create booking
         */
        processStripePayment: async function(form, data, eventType, submitBtn, buttonText, loadingText) {
            try {
                if (!window.GDCMStripePayment) {
                    throw new Error('Payment system not available');
                }

                // Prepare booking data
                let bookingData;
                if (eventType === 'party_slot' || eventType === 'party') {
                    const eventDate = new Date(this.currentEvent.start);
                    const selectedStartTime = `${data.party_start_time}:00`;
                    const durationMinutes = parseInt(data.party_duration) * 60;
                    const startTimeParts = data.party_start_time.split(':');
                    const startDate = new Date();
                    startDate.setHours(parseInt(startTimeParts[0]), parseInt(startTimeParts[1]), 0, 0);
                    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
                    const selectedEndTime = `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}:00`;

                    bookingData = {
                        event_id: parseInt(data.event_id),
                        party_slot_id: parseInt(data.event_id),
                        booking_type: 'party',
                        event_name: data.party_type || 'Party Event',
                        customer_name: data.contact_name,
                        customer_email: data.contact_email,
                        phone_number: data.contact_phone,
                        party_size: parseInt(data.party_size),
                        party_duration: durationMinutes,
                        selected_start_time: selectedStartTime,
                        selected_end_time: selectedEndTime,
                        special_requests: data.special_requests || '',
                        total_price: this.calculateBookingPrice(eventType, data)
                    };
                } else {
                    bookingData = {
                        event_id: parseInt(data.event_id),
                        booking_type: 'class',
                        customer_name: data.parent_name,
                        parent_name: data.parent_name,
                        customer_email: data.parent_email,
                        parent_email: data.parent_email,
                        phone_number: data.parent_phone,
                        child_name: data.child_name,
                        child_birthday: data.child_birthday || null,
                        child_age: parseInt(data.child_age),
                        number_of_children: parseInt(data.num_children) || 1,
                        special_requests: data.special_requests || '',
                        total_price: this.calculateBookingPrice(eventType, data)
                    };
                }

                // Process payment
                buttonText.textContent = 'Processing payment...';
                const result = await window.GDCMStripePayment.processPayment(bookingData);

                if (result.success) {
                    // Reset processing flag
                    this.isProcessing = false;
                    
                    // Reset payment form
                    window.GDCMStripePayment.reset();
                    
                    // Show success
                    this.showSuccessModal({
                        booking_id: result.bookingId,
                        status: 'confirmed',
                        payment_amount: result.amountPaid
                    });
                    this.closeModal();
                    
                    if (this.calendar) {
                        this.calendar.refetchEvents();
                    }
                } else {
                    throw new Error(result.message || 'Payment failed');
                }

            } catch (error) {
                console.error('Payment error:', error);
                // Reset processing flag on error
                this.isProcessing = false;
                window.GDCMStripePayment.showError(error.message || 'Payment failed. Please try again.');
                buttonText.textContent = 'Pay & Confirm Booking';
                buttonText.classList.remove('tw-hidden');
                loadingText.classList.add('tw-hidden');
                submitBtn.disabled = false;
                submitBtn.style.pointerEvents = ''; // Re-enable clicks
            }
        },

        /**
         * Submit booking without payment
         */
        submitBookingWithoutPayment: function(data, eventType, submitBtn, buttonText, loadingText) {
            // Prepare data based on event type
            let endpoint = 'book';
            let requestData = data;

            if (eventType === 'party_slot' || eventType === 'party') {
                // Use party booking endpoint and transform field names
                endpoint = 'party-bookings';
                
                // Get the event date
                const eventDate = new Date(this.currentEvent.start);
                const dateStr = eventDate.toISOString().split('T')[0]; // YYYY-MM-DD format
                
                // Extract just the time part for database storage
                const selectedStartTime = `${data.party_start_time}:00`; // Just time format HH:MM:SS
                
                // Calculate end time based on duration
                const durationMinutes = parseInt(data.party_duration) * 60; // Convert hours to minutes
                const startTimeParts = data.party_start_time.split(':');
                const startDate = new Date();
                startDate.setHours(parseInt(startTimeParts[0]), parseInt(startTimeParts[1]), 0, 0);
                const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
                const selectedEndTime = `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}:00`;
                
                requestData = {
                    party_slot_id: data.event_id,
                    event_name: data.party_type || 'Party Event',
                    customer_name: data.contact_name,
                    customer_email: data.contact_email,
                    customer_phone: data.contact_phone,
                    num_guests: parseInt(data.party_size),
                    selected_duration: durationMinutes,
                    selected_start_time: selectedStartTime,
                    selected_end_time: selectedEndTime,
                    event_description: data.party_type || '',
                    special_requests: data.special_requests || '',
                    selected_package: 'Standard Package' // Default package
                };
            }

            fetch(gdcmData.root + endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': gdcmData.nonce
                },
                body: JSON.stringify(requestData)
            })
            .then(response => response.json())
            .then(result => {
                // Reset processing flag
                this.isProcessing = false;
                
                if (result.success) {
                    this.showSuccessModal(result.data);
                    this.closeModal();
                    this.calendar.refetchEvents(); // Refresh to show updated availability
                } else {
                    this.showNotification(result.message || 'Error submitting booking', 'error');
                }
            })
            .catch(error => {
                console.error('Error:', error);
                // Reset processing flag on error
                this.isProcessing = false;
                this.showNotification('Network error occurred. Please try again.', 'error');
            })
            .finally(() => {
                // Reset button state
                buttonText.classList.remove('tw-hidden');
                loadingText.classList.add('tw-hidden');
                submitBtn.disabled = false;
                submitBtn.style.pointerEvents = ''; // Re-enable clicks
            });
        },

        /**
         * Calculate booking price - uses the fixed price from admin configuration
         */
        calculateBookingPrice: function(eventType, data) {
            // Always use the fixed price from admin configuration
            return parseFloat(gdcmData.payment?.defaultPrice) || 20.00;
        },

        validateForm: function(form) {
            let isValid = true;
            const requiredFields = form.querySelectorAll('[required]');
            const eventType = this.currentEvent?.extendedProps?.type || 'class';
            
            requiredFields.forEach(field => {
                if (!field.value.trim()) {
                    this.showFieldError(field, 'This field is required');
                    isValid = false;
                } else {
                    this.clearFieldError(field);
                }
            });

            // Validate email for both party and class forms
            const emailInput = form.querySelector('#contact-email, #parent-email');
            if (emailInput && emailInput.value && !this.isValidEmail(emailInput.value)) {
                this.showFieldError(emailInput, 'Please enter a valid email address');
                isValid = false;
            }

            // Validate party time selection
            if (eventType === 'party_slot' || eventType === 'party') {
                const startTimeInput = form.querySelector('#party-start-time');
                const durationInput = form.querySelector('#party-duration');
                
                if (startTimeInput && durationInput) {
                    const startTime = startTimeInput.value;
                    const duration = parseInt(durationInput.value);
                    
                    if (!startTime) {
                        this.showFieldError(startTimeInput, 'Please select a start time');
                        isValid = false;
                    }
                    
                    if (!duration || duration < 1) {
                        this.showFieldError(durationInput, 'Please select a valid duration');
                        isValid = false;
                    }
                    
                    // Validate that the selected time slot doesn't exceed available time
                    if (startTime && duration && this.currentEvent) {
                        const eventStart = new Date(this.currentEvent.start);
                        const eventEnd = new Date(this.currentEvent.end);
                        const availableHours = Math.round((eventEnd - eventStart) / (1000 * 60 * 60));
                        
                        const [hours, minutes] = startTime.split(':').map(Number);
                        const selectedStart = new Date(eventStart);
                        selectedStart.setHours(hours, minutes, 0, 0);
                        
                        const selectedEnd = new Date(selectedStart);
                        selectedEnd.setHours(selectedEnd.getHours() + duration);
                        
                        if (selectedEnd > eventEnd) {
                            this.showFieldError(durationInput, `Maximum duration for this start time is ${Math.floor((eventEnd - selectedStart) / (1000 * 60 * 60))} hours`);
                            isValid = false;
                        }
                    }
                }
            }

            // Validate age against event requirements (for class bookings only)
            if (eventType === 'class') {
                const ageInput = form.querySelector('#child-age');
                if (ageInput && ageInput.value && this.currentEvent) {
                    const age = parseInt(ageInput.value);
                    const ageValidation = this.validateAgeForEvent(age, this.currentEvent.extendedProps);
                    if (!ageValidation.valid) {
                        this.showFieldError(ageInput, ageValidation.message);
                        isValid = false;
                    }
                }
            }

            return isValid;
        },

        validateAge: function(input) {
            if (!this.currentEvent) return;
            
            const age = parseInt(input.value);
            const validation = this.validateAgeForEvent(age, this.currentEvent.extendedProps);
            const messageEl = input.parentNode.querySelector('.age-validation-message');
            
            if (messageEl) {
                if (validation.valid) {
                    messageEl.classList.add('tw-hidden');
                    messageEl.classList.remove('tw-text-red-600', 'tw-text-yellow-600');
                } else {
                    messageEl.classList.remove('tw-hidden');
                    messageEl.classList.add(validation.warning ? 'tw-text-yellow-600' : 'tw-text-red-600');
                    messageEl.textContent = validation.message;
                }
            }
        },

        validateAgeForEvent: function(age, eventProps) {
            if (!age || isNaN(age)) return { valid: false, message: 'Please enter a valid age' };
            
            const minAge = eventProps.age_min;
            const maxAge = eventProps.age_max;
            
            if (minAge && age < minAge) {
                return { valid: false, message: `Minimum age for this event is ${minAge}` };
            }
            
            if (maxAge && age > maxAge) {
                return { valid: false, message: `Maximum age for this event is ${maxAge}` };
            }
            
            return { valid: true };
        },

        validateEmail: function(input) {
            const isValid = this.isValidEmail(input.value);
            if (isValid) {
                this.clearFieldError(input);
            } else if (input.value) {
                this.showFieldError(input, 'Please enter a valid email address');
            }
        },

        isValidEmail: function(email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email);
        },

        showFieldError: function(field, message) {
            field.classList.add('tw-border-red-500');
            field.classList.remove('tw-border-gray-300');
            
            let errorEl = field.parentNode.querySelector('.field-error');
            if (!errorEl) {
                errorEl = document.createElement('div');
                errorEl.className = 'field-error tw-text-red-600 tw-text-sm tw-mt-1';
                field.parentNode.appendChild(errorEl);
            }
            errorEl.textContent = message;
        },

        clearFieldError: function(field) {
            field.classList.remove('tw-border-red-500');
            field.classList.add('tw-border-gray-300');
            
            const errorEl = field.parentNode.querySelector('.field-error');
            if (errorEl) {
                errorEl.remove();
            }
        },

        showSuccessModal: function(bookingData) {
            const modalHtml = `
                <div class="gdcm-modal" id="gdcm-success-modal">
                    <div class="gdcm-modal-content">
                        <div class="gdcm-modal-header tw-bg-green-600">
                            <h3 class="gdcm-modal-title">Booking Successful!</h3>
                        </div>
                        <div class="gdcm-modal-body tw-text-center">
                            <div class="tw-mb-6">
                                <div class="tw-w-16 tw-h-16 tw-bg-green-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4">
                                    <svg class="tw-w-8 tw-h-8 tw-text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                                    </svg>
                                </div>
                                <h4 class="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-2">Your booking has been confirmed!</h4>
                                <p class="tw-text-gray-600">Booking ID: <span class="tw-font-mono tw-font-semibold">${bookingData.booking_id}</span></p>
                            </div>
                            
                            <div class="tw-bg-gray-50 tw-p-4 tw-rounded-lg tw-text-left tw-mb-6">
                                <h5 class="tw-font-semibold tw-mb-2">Next Steps:</h5>
                                <ul class="tw-text-sm tw-space-y-1 tw-text-gray-700">
                                    <li>• Check your email for booking confirmation</li>
                                    <li>• You'll receive reminder notifications 24 hours before the event</li>
                                    <li>• Arrive 10 minutes early for check-in</li>
                                    <li>• Contact us if you need to make changes</li>
                                </ul>
                            </div>
                            
                            <button type="button" class="gdcm-button gdcm-modal-close">Got it!</button>
                        </div>
                    </div>
                </div>
            `;

            document.body.insertAdjacentHTML('beforeend', modalHtml);
            const successModal = document.getElementById('gdcm-success-modal');
            successModal.classList.remove('hidden');
            
            // Auto-close success modal after 5 seconds
            setTimeout(() => {
                if (successModal && !successModal.classList.contains('hidden')) {
                    successModal.remove();
                }
            }, 5000);
        },

        filterEventsByType: function(type) {
            if (!this.calendar) return;
            
            // Update the events URL with the filter parameter
            let url = gdcmData.root + 'events';
            if (type && type !== 'all') {
                // Check if URL already has query parameters
                const separator = url.includes('?') ? '&' : '?';
                url += separator + 'type=' + encodeURIComponent(type);
            }
            
            // Update the calendar's events URL and refetch
            this.calendar.options.events = url;
            this.calendar.refetchEvents();
        },

        getEventTooltip: function(event) {
            const type = event.extendedProps.type || 'class';
            const startDate = new Date(event.start);
            const time = startDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
            const duration = event.extendedProps.duration || 60;
            return `${event.title} (${type}) - ${time} (${duration} min)`;
        },

        getAgeRange: function(eventProps) {
            const minAge = eventProps.age_min;
            const maxAge = eventProps.age_max;
            
            if (minAge && maxAge) {
                return `${minAge}-${maxAge} years`;
            } else if (minAge) {
                return `${minAge}+ years`;
            } else if (maxAge) {
                return `Up to ${maxAge} years`;
            } else {
                return 'All ages';
            }
        },

        closeModal: function() {
            if (this.modal) {
                this.modal.remove();
                this.modal = null;
            }
            this.currentEvent = null;
            // Reset processing flag when modal is closed
            this.isProcessing = false;
        },

        showNotification: function(message, type = 'info') {
            const notification = document.createElement('div');
            notification.className = `gdcm-notification tw-fixed tw-top-4 tw-right-4 tw-z-50 tw-p-4 tw-rounded-lg tw-shadow-lg tw-max-w-sm tw-animate-fade-in`;
            
            const bgColor = type === 'success' ? 'tw-bg-green-100 tw-text-green-800 tw-border tw-border-green-200' : 
                           type === 'error' ? 'tw-bg-red-100 tw-text-red-800 tw-border tw-border-red-200' : 
                           'tw-bg-blue-100 tw-text-blue-800 tw-border tw-border-blue-200';
            
            notification.classList.add(...bgColor.split(' '));
            notification.textContent = message;

            document.body.appendChild(notification);

            // Auto remove after 4 seconds
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.style.opacity = '0';
                    notification.style.transform = 'translateX(100%)';
                    setTimeout(() => notification.remove(), 300);
                }
            }, 4000);

            // Remove on click
            notification.addEventListener('click', () => {
                notification.style.opacity = '0';
                notification.style.transform = 'translateX(100%)';
                setTimeout(() => notification.remove(), 300);
            });
        },

        // Utility functions
        debounce: function(func, wait) {
            let timeout;
            return function executedFunction(...args) {
                const later = () => {
                    clearTimeout(timeout);
                    func(...args);
                };
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
            };
        }
    };

    // Initialize the frontend interface
    GDCMFrontend.init();

    // Make it globally available for debugging
    window.GDCMFrontend = GDCMFrontend;
    
    // ============================================
    // Table Booking Module
    // ============================================
    const GDCMTableBooking = {
        calendar: null,
        modal: null,
        selectedDate: null,
        selectedTimeSlot: null,
        selectedTables: [],
        numGuests: 2,
        numChildren: 0,
        numHighChairs: 0,
        availableTables: [],
        timeSlots: [],
        isProcessing: false,
        
        settings: {
            pricePerPerson: 25.00,
            pricePerChild: 0.00,
            childMinAge: 4,
            tableCapacity: 2,
            maxHours: 2,
            currency: '$',
            allowTableSelection: true
        },
        
        /**
         * Build a proper REST API URL handling both pretty and plain permalinks
         * @param {string} endpoint - The endpoint path (e.g., 'tables/available')
         * @param {object} params - Query parameters as key-value pairs
         * @returns {string} The complete URL
         */
        buildRestUrl: function(endpoint, params = {}) {
            const root = gdcmData.root;
            const queryString = Object.entries(params)
                .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
                .join('&');
            
            // Check if root already contains a query string (plain permalinks)
            if (root.includes('?')) {
                // Plain permalinks: append endpoint to rest_route and add params with &
                const url = root + endpoint;
                return queryString ? `${url}&${queryString}` : url;
            } else {
                // Pretty permalinks: use standard URL format
                const url = root + endpoint;
                return queryString ? `${url}?${queryString}` : url;
            }
        },
        
        init: function() {
            const calendarEl = document.getElementById('gdcm-table-booking-calendar');
            if (!calendarEl) return;
            
            // Skip if already initialized
            if (calendarEl.hasAttribute('data-calendar-initialized')) {
                return;
            }
            calendarEl.setAttribute('data-calendar-initialized', 'true');
            
            // Load settings from data attributes
            const container = document.querySelector('.gdcm-table-booking-container');
            if (container) {
                this.settings.pricePerPerson = parseFloat(container.dataset.pricePerPerson) || 25.00;
                this.settings.tableCapacity = parseInt(container.dataset.tableCapacity) || 2;
                this.settings.maxHours = parseInt(container.dataset.maxHours) || 2;
                this.settings.currency = container.dataset.currency || '$';
                this.settings.allowTableSelection = container.dataset.allowTableSelection === 'true';
            }
            
            // Also load from gdcmData if available (overrides data attributes)
            if (gdcmData && gdcmData.tableBooking) {
                if (typeof gdcmData.tableBooking.allowTableSelection !== 'undefined') {
                    this.settings.allowTableSelection = gdcmData.tableBooking.allowTableSelection === true;
                }
                if (typeof gdcmData.tableBooking.pricePerChild !== 'undefined') {
                    this.settings.pricePerChild = parseFloat(gdcmData.tableBooking.pricePerChild) || 0.00;
                }
                if (typeof gdcmData.tableBooking.childMinAge !== 'undefined') {
                    this.settings.childMinAge = parseInt(gdcmData.tableBooking.childMinAge) || 4;
                }
            }
            
            this.initCalendar(calendarEl);
            this.bindEvents();
            this.loadTimeSlots();
        },
        
        initCalendar: function(calendarEl) {
            if (!window.GDCMCalendar) {
                console.error('GDCMCalendar not available');
                return;
            }
            
            this.calendar = new window.GDCMCalendar(calendarEl, {
                initialView: 'dayGridMonth',
                headerToolbar: {
                    left: 'prev,next,today',
                    center: 'title',
                    right: ''
                },
                events: this.fetchDatesWithAvailability.bind(this),
                dateClick: (info) => {
                    this.handleDayClick(info.date);
                },
                isAdmin: false,
                height: 'auto',
                aspectRatio: 1.6,
                firstDay: 1,
                selectable: true,
                selectMirror: true
            });
            
            this.calendar.render();
        },
        
        fetchDatesWithAvailability: function(fetchInfo, successCallback, failureCallback) {
            // Convert Date objects to ISO strings
            const startStr = fetchInfo.start instanceof Date 
                ? fetchInfo.start.toISOString().split('T')[0] 
                : (fetchInfo.startStr || fetchInfo.start);
            const endStr = fetchInfo.end instanceof Date 
                ? fetchInfo.end.toISOString().split('T')[0] 
                : (fetchInfo.endStr || fetchInfo.end);
            
            fetch(
                this.buildRestUrl('table-booking-dates', { start: startStr, end: endStr }),
                {
                    headers: {
                        'X-WP-Nonce': gdcmData.nonce
                    }
                }
            )
            .then(response => response.json())
            .then(dates => {
                // Convert to calendar events for styling
                const events = dates.map(d => ({
                    start: d.date,
                    allDay: true,
                    display: 'background',
                    className: d.available ? 'gdcm-date-available' : 'gdcm-date-unavailable',
                    extendedProps: {
                        available: d.available,
                        status: d.status
                    }
                }));
                
                if (successCallback) {
                    successCallback(events);
                }
            })
            .catch(error => {
                console.error('Error fetching table dates:', error);
                if (failureCallback) {
                    failureCallback(error);
                } else if (successCallback) {
                    successCallback([]);
                }
            });
        },
        
        handleDayClick: function(date) {
            const dateStr = date.toISOString().split('T')[0];
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            if (date < today) {
                GDCMFrontend.showNotification('Cannot book past dates', 'error');
                return;
            }
            
            this.selectedDate = dateStr;
            this.selectedTimeSlot = null;
            this.selectedTables = [];
            this.numGuests = 2;
            this.numChildren = 0;
            this.numHighChairs = 0;
            
            this.openModal();
        },
        
        loadTimeSlots: async function() {
            try {
                const response = await fetch(this.buildRestUrl('table-time-slots'), {
                    headers: { 'X-WP-Nonce': gdcmData.nonce }
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const data = await response.json();
                this.timeSlots = Array.isArray(data) ? data : [];
                console.log('Time slots loaded:', this.timeSlots.length);
            } catch (error) {
                console.error('Error loading time slots:', error);
                this.timeSlots = [];
            }
        },
        
        openModal: function() {
            this.modal = document.getElementById('gdcm-table-booking-modal');
            if (!this.modal) return;
            
            // Update modal date display
            const dateDisplay = this.modal.querySelector('#modal-selected-date');
            if (dateDisplay) {
                const date = new Date(this.selectedDate + 'T12:00:00');
                dateDisplay.textContent = date.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }
            
            // Reset steps
            this.showStep(1);
            this.renderTimeSlots();
            
            // Show modal
            this.modal.classList.remove('tw-hidden');
            document.body.style.overflow = 'hidden';
            
            // Reset display counters
            const guestDisplay = this.modal.querySelector('#guest-count-display');
            if (guestDisplay) guestDisplay.textContent = this.numGuests;
            
            const childrenDisplay = this.modal.querySelector('#children-count-display');
            if (childrenDisplay) childrenDisplay.textContent = this.numChildren;
            
            const highChairsDisplay = this.modal.querySelector('#high-chairs-count-display');
            if (highChairsDisplay) highChairsDisplay.textContent = this.numHighChairs;
            
            this.updateSeatsDisplay();
        },
        
        closeModal: function() {
            if (this.modal) {
                this.modal.classList.add('tw-hidden');
                document.body.style.overflow = '';
                
                // Clean up payment form if it was shown
                const paymentContainer = this.modal.querySelector('#gdcm-table-payment-container');
                if (paymentContainer) {
                    paymentContainer.remove();
                }
                
                // Reset submit button to original state
                const submitBtn = this.modal.querySelector('#submit-booking-btn');
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.innerHTML = 'Confirm Reservation';
                }
                
                // Reset form fields
                const form = this.modal.querySelector('#table-booking-form');
                if (form) {
                    form.reset();
                }
            }
            this.selectedTimeSlot = null;
            this.selectedTables = [];
            this.numGuests = 2;
            this.numChildren = 0;
            this.numHighChairs = 0;
            this.isProcessing = false;
            this.paymentFormShown = false;
            
            // Reset Stripe payment state
            if (window.GDCMStripePayment) {
                window.GDCMStripePayment.reset();
            }
        },
        
        showStep: function(step) {
            const steps = this.modal.querySelectorAll('.modal-step');
            steps.forEach(s => s.classList.add('tw-hidden'));
            
            const activeStep = this.modal.querySelector(`#step-${step}`);
            if (activeStep) {
                activeStep.classList.remove('tw-hidden');
            }
            
            // Update step indicators
            const indicators = this.modal.querySelectorAll('.step-indicator');
            indicators.forEach((ind, idx) => {
                if (idx + 1 < step) {
                    ind.classList.add('completed');
                    ind.classList.remove('active');
                } else if (idx + 1 === step) {
                    ind.classList.add('active');
                    ind.classList.remove('completed');
                } else {
                    ind.classList.remove('active', 'completed');
                }
            });
        },
        
        renderTimeSlots: async function() {
            const container = this.modal.querySelector('#time-slots-container');
            if (!container) return;
            
            container.innerHTML = '<div class="tw-text-center tw-py-4"><div class="tw-animate-spin tw-inline-block tw-w-8 tw-h-8 tw-border-4 tw-border-amber-500 tw-border-t-transparent tw-rounded-full"></div></div>';
            
            try {
                // First, ensure time slots are loaded
                if (!this.timeSlots || this.timeSlots.length === 0) {
                    await this.loadTimeSlots();
                }
                
                // Check if we have time slots now
                if (!this.timeSlots || this.timeSlots.length === 0) {
                    container.innerHTML = '<div class="tw-text-gray-500 tw-text-center tw-py-4">No time slots available. Please contact us to make a reservation.</div>';
                    return;
                }
                
                // Get available tables for each time slot
                const slotsHtml = await Promise.all(this.timeSlots.map(async slot => {
                    try {
                        const response = await fetch(
                            this.buildRestUrl('tables/available', { date: this.selectedDate, time_slot: slot.start + '-' + slot.end }),
                            { headers: { 'X-WP-Nonce': gdcmData.nonce } }
                        );
                        const tables = await response.json();
                        const availableCount = Array.isArray(tables) ? tables.filter(t => t.is_available).length : 0;
                        const isAvailable = availableCount > 0;
                        
                        return `
                            <button type="button" 
                                class="time-slot-btn tw-p-4 tw-rounded-lg tw-border tw-text-left tw-transition-all ${isAvailable ? 
                                    'tw-border-gray-200 hover:tw-border-amber-400 hover:tw-bg-amber-50 tw-cursor-pointer' : 
                                    'tw-border-gray-100 tw-bg-gray-50 tw-text-gray-400 tw-cursor-not-allowed'}"
                                data-slot="${slot.start}-${slot.end}"
                                ${!isAvailable ? 'disabled' : ''}>
                                <div class="tw-font-semibold">${slot.label}</div>
                                <div class="tw-text-sm ${isAvailable ? 'tw-text-green-600' : 'tw-text-gray-400'}">
                                    ${isAvailable ? `${availableCount} table${availableCount > 1 ? 's' : ''} available` : 'No tables available'}
                                </div>
                            </button>
                        `;
                    } catch (slotError) {
                        console.error('Error fetching availability for slot:', slot, slotError);
                        return `
                            <button type="button" 
                                class="time-slot-btn tw-p-4 tw-rounded-lg tw-border tw-text-left tw-border-gray-200"
                                data-slot="${slot.start}-${slot.end}">
                                <div class="tw-font-semibold">${slot.label}</div>
                                <div class="tw-text-sm tw-text-gray-400">Check availability</div>
                            </button>
                        `;
                    }
                }));
                
                container.innerHTML = `<div class="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-3">${slotsHtml.join('')}</div>`;
                
                // Bind click events
                container.querySelectorAll('.time-slot-btn:not([disabled])').forEach(btn => {
                    btn.addEventListener('click', () => {
                        container.querySelectorAll('.time-slot-btn').forEach(b => b.classList.remove('tw-border-amber-500', 'tw-bg-amber-50'));
                        btn.classList.add('tw-border-amber-500', 'tw-bg-amber-50');
                        this.selectedTimeSlot = btn.dataset.slot;
                        
                        // Enable next button
                        const nextBtn = this.modal.querySelector('#step1-next');
                        if (nextBtn) nextBtn.disabled = false;
                    });
                });
            } catch (error) {
                container.innerHTML = '<div class="tw-text-red-500 tw-text-center tw-py-4">Error loading time slots</div>';
            }
        },
        
        // Get total seats needed (adults + children)
        getTotalSeats: function() {
            return this.numGuests + this.numChildren;
        },
        
        // Update the seats and tables display
        updateSeatsDisplay: function() {
            const totalSeats = this.getTotalSeats();
            const tablesNeeded = Math.ceil(totalSeats / this.settings.tableCapacity);
            
            const totalSeatsDisplay = this.modal.querySelector('#total-seats-display');
            if (totalSeatsDisplay) totalSeatsDisplay.textContent = totalSeats;
            
            const tablesDisplay = this.modal.querySelector('#tables-needed-display');
            if (tablesDisplay) tablesDisplay.textContent = tablesNeeded;
        },
        
        updateGuestCount: function(delta) {
            const newCount = this.numGuests + delta;
            if (newCount >= 1 && newCount <= 20) {
                this.numGuests = newCount;
                
                const display = this.modal.querySelector('#guest-count-display');
                if (display) display.textContent = this.numGuests;
                
                this.updateSeatsDisplay();
                this.updatePriceSummary();
            }
        },
        
        updateChildrenCount: function(delta) {
            const newCount = this.numChildren + delta;
            if (newCount >= 0 && newCount <= 10) {
                this.numChildren = newCount;
                
                const display = this.modal.querySelector('#children-count-display');
                if (display) display.textContent = this.numChildren;
                
                this.updateSeatsDisplay();
                this.updatePriceSummary();
            }
        },
        
        updateHighChairsCount: function(delta) {
            const newCount = this.numHighChairs + delta;
            if (newCount >= 0 && newCount <= 5) {
                this.numHighChairs = newCount;
                
                const display = this.modal.querySelector('#high-chairs-count-display');
                if (display) display.textContent = this.numHighChairs;
            }
        },
        
        loadAvailableTables: async function() {
            const container = this.modal.querySelector('#floor-plan-container');
            if (!container || !this.selectedTimeSlot) return;
            
            container.innerHTML = '<div class="tw-text-center tw-py-8"><div class="tw-animate-spin tw-inline-block tw-w-8 tw-h-8 tw-border-4 tw-border-amber-500 tw-border-t-transparent tw-rounded-full"></div></div>';
            
            try {
                const response = await fetch(
                    this.buildRestUrl('tables/available', { date: this.selectedDate, time_slot: this.selectedTimeSlot }),
                    { headers: { 'X-WP-Nonce': gdcmData.nonce } }
                );
                this.availableTables = await response.json();
                this.selectedTables = [];
                
                this.renderFloorPlan(container);
            } catch (error) {
                container.innerHTML = '<div class="tw-text-red-500 tw-text-center tw-py-4">Error loading tables</div>';
            }
        },
        
        /**
         * Load available tables for automatic selection (without rendering UI)
         */
        loadAvailableTablesForAutoSelect: async function() {
            if (!this.selectedTimeSlot) return false;
            
            try {
                const response = await fetch(
                    this.buildRestUrl('tables/available', { date: this.selectedDate, time_slot: this.selectedTimeSlot }),
                    { headers: { 'X-WP-Nonce': gdcmData.nonce } }
                );
                this.availableTables = await response.json();
                this.selectedTables = [];
                return true;
            } catch (error) {
                console.error('Error loading tables for auto-select:', error);
                GDCMFrontend.showNotification('Error loading available tables', 'error');
                return false;
            }
        },
        
        renderFloorPlan: function(container) {
            const totalSeats = this.getTotalSeats();
            const tablesNeeded = Math.ceil(totalSeats / this.settings.tableCapacity);
            const positionScale = 30; // Scale factor: each unit = 30 pixels
            const padding = 40; // Extra padding around the tables
            
            // Calculate required dimensions based on table positions
            let maxX = 0;
            let maxY = 0;
            
            this.availableTables.forEach(table => {
                const posX = parseInt(table.position_x) * positionScale + (parseInt(table.width) || 60);
                const posY = parseInt(table.position_y) * positionScale + (parseInt(table.height) || 60);
                if (posX > maxX) maxX = posX;
                if (posY > maxY) maxY = posY;
            });
            
            // Set minimum dimensions, but grow if tables require more space
            const floorPlanWidth = Math.max(600, maxX + padding);
            const floorPlanHeight = Math.max(350, maxY + padding);
            
            let html = `
                <div class="tw-mb-4 tw-text-sm tw-text-gray-600">
                    Select ${tablesNeeded} table${tablesNeeded > 1 ? 's' : ''} for ${totalSeats} people (${this.numGuests} adult${this.numGuests > 1 ? 's' : ''}${this.numChildren > 0 ? ` + ${this.numChildren} child${this.numChildren > 1 ? 'ren' : ''}` : ''})
                </div>
                <div class="floor-plan-wrapper tw-overflow-auto tw-rounded-lg tw-border tw-border-gray-200" style="max-height: 400px;">
                    <div class="floor-plan tw-relative tw-bg-gray-100 tw-p-4" style="width: ${floorPlanWidth}px; height: ${floorPlanHeight}px; min-width: ${floorPlanWidth}px; min-height: ${floorPlanHeight}px;">
            `;
            
            this.availableTables.forEach(table => {
                const isAvailable = table.is_available;
                const isSelected = this.selectedTables.includes(table.id);
                
                const shapeClass = table.shape === 'round' ? 'tw-rounded-full' : 'tw-rounded-lg';
                const statusClass = !isAvailable ? 'tw-bg-red-200 tw-border-red-300 tw-cursor-not-allowed' :
                                   isSelected ? 'tw-bg-amber-400 tw-border-amber-500' :
                                   'tw-bg-green-200 tw-border-green-300 hover:tw-bg-green-300 tw-cursor-pointer';
                
                const posX = parseInt(table.position_x) * positionScale;
                const posY = parseInt(table.position_y) * positionScale;
                
                html += `
                    <div class="floor-table tw-absolute tw-flex tw-items-center tw-justify-center tw-border-2 tw-font-bold tw-text-sm tw-transition-all ${shapeClass} ${statusClass}"
                        style="left: ${posX}px; top: ${posY}px; width: ${table.width || 60}px; height: ${table.height || 60}px;"
                        data-table-id="${table.id}"
                        data-available="${isAvailable}">
                        ${table.table_number}
                    </div>
                `;
            });
            
            html += `</div></div>
                <p class="tw-text-xs tw-text-gray-400 tw-mt-2 tw-text-center">Scroll to see all tables</p>
                <div class="tw-mt-4 tw-flex tw-gap-4 tw-text-sm">
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <div class="tw-w-4 tw-h-4 tw-bg-green-200 tw-border tw-border-green-300 tw-rounded"></div>
                        <span>Available</span>
                    </div>
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <div class="tw-w-4 tw-h-4 tw-bg-amber-400 tw-border tw-border-amber-500 tw-rounded"></div>
                        <span>Selected</span>
                    </div>
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <div class="tw-w-4 tw-h-4 tw-bg-red-200 tw-border tw-border-red-300 tw-rounded"></div>
                        <span>Booked</span>
                    </div>
                </div>
            `;
            
            container.innerHTML = html;
            
            // Bind click events
            container.querySelectorAll('.floor-table[data-available="true"]').forEach(tableEl => {
                tableEl.addEventListener('click', () => {
                    const tableId = parseInt(tableEl.dataset.tableId);
                    this.toggleTableSelection(tableId, tableEl, tablesNeeded);
                });
            });
        },
        
        toggleTableSelection: function(tableId, tableEl, tablesNeeded) {
            const index = this.selectedTables.indexOf(tableId);
            
            if (index > -1) {
                // Deselect
                this.selectedTables.splice(index, 1);
                tableEl.classList.remove('tw-bg-amber-400', 'tw-border-amber-500');
                tableEl.classList.add('tw-bg-green-200', 'tw-border-green-300');
            } else {
                // Select (check limit)
                if (this.selectedTables.length >= tablesNeeded) {
                    GDCMFrontend.showNotification(`You only need ${tablesNeeded} table${tablesNeeded > 1 ? 's' : ''}`, 'info');
                    return;
                }
                this.selectedTables.push(tableId);
                tableEl.classList.remove('tw-bg-green-200', 'tw-border-green-300');
                tableEl.classList.add('tw-bg-amber-400', 'tw-border-amber-500');
            }
            
            // Update next button state
            const nextBtn = this.modal.querySelector('#step3-next');
            if (nextBtn) {
                nextBtn.disabled = this.selectedTables.length !== tablesNeeded;
            }
            
            // Update selected count display
            const selectedDisplay = this.modal.querySelector('#selected-tables-count');
            if (selectedDisplay) {
                selectedDisplay.textContent = `${this.selectedTables.length}/${tablesNeeded}`;
            }
        },
        
        /**
         * Automatically select optimal tables based on guest count
         * Uses a smart algorithm to minimize the number of tables needed
         * while ensuring sufficient capacity for all guests
         */
        autoSelectTables: function() {
            const totalSeats = this.getTotalSeats();
            const availableTables = this.availableTables.filter(t => t.is_available);
            
            if (availableTables.length === 0) {
                GDCMFrontend.showNotification('No tables available for this time slot', 'error');
                return false;
            }
            
            // Sort tables by capacity (descending) to optimize selection
            const sortedTables = [...availableTables].sort((a, b) => {
                const capA = parseInt(a.capacity) || this.settings.tableCapacity;
                const capB = parseInt(b.capacity) || this.settings.tableCapacity;
                return capB - capA;
            });
            
            // Find optimal combination of tables
            const selectedTables = this.findOptimalTableCombination(sortedTables, totalSeats);
            
            if (selectedTables.length === 0) {
                GDCMFrontend.showNotification('Not enough table capacity for your group size', 'error');
                return false;
            }
            
            this.selectedTables = selectedTables.map(t => t.id);
            
            return true;
        },
        
        /**
         * Find the optimal combination of tables to seat the given number of guests
         * Tries to minimize wasted capacity while using the fewest tables
         * @param {Array} tables - Available tables sorted by capacity (descending)
         * @param {number} numGuests - Number of guests to seat
         * @returns {Array} - Selected tables
         */
        findOptimalTableCombination: function(tables, numGuests) {
            const defaultCapacity = this.settings.tableCapacity;
            
            // Get tables with their actual capacities
            const tablesWithCapacity = tables.map(t => ({
                ...t,
                actualCapacity: parseInt(t.capacity) || defaultCapacity
            }));
            
            // First, try to find a single table that fits exactly or with minimal waste
            const singleTable = tablesWithCapacity.find(t => t.actualCapacity >= numGuests);
            if (singleTable && singleTable.actualCapacity <= numGuests + 2) {
                // Single table with at most 2 extra seats - good enough
                return [singleTable];
            }
            
            // Try to find the best combination using greedy approach
            let remainingGuests = numGuests;
            const selected = [];
            const usedIds = new Set();
            
            // Sort by capacity descending for greedy selection
            const sortedByCapacity = [...tablesWithCapacity].sort((a, b) => b.actualCapacity - a.actualCapacity);
            
            while (remainingGuests > 0) {
                // Find the best table for remaining guests
                // Prefer tables that fit exactly or with minimal waste
                let bestTable = null;
                let bestWaste = Infinity;
                
                for (const table of sortedByCapacity) {
                    if (usedIds.has(table.id)) continue;
                    
                    const capacity = table.actualCapacity;
                    
                    // If this table can fit all remaining guests
                    if (capacity >= remainingGuests) {
                        const waste = capacity - remainingGuests;
                        if (waste < bestWaste) {
                            bestWaste = waste;
                            bestTable = table;
                        }
                    } else if (!bestTable && capacity > 0) {
                        // If no single table fits, use the largest available
                        bestTable = table;
                        bestWaste = 0; // No waste since we need more tables
                    }
                }
                
                if (!bestTable) {
                    // Can't accommodate all guests
                    return [];
                }
                
                selected.push(bestTable);
                usedIds.add(bestTable.id);
                remainingGuests -= bestTable.actualCapacity;
            }
            
            return selected;
        },
        
        updatePriceSummary: function() {
            const summaryEl = this.modal.querySelector('#price-summary');
            if (!summaryEl) return;
            
            const adultPrice = this.numGuests * this.settings.pricePerPerson;
            const childPrice = this.numChildren * this.settings.pricePerChild;
            const totalPrice = adultPrice + childPrice;
            
            // Show auto-assigned tables info if table selection is disabled
            let tablesInfo = '';
            if (!this.settings.allowTableSelection && this.selectedTables.length > 0) {
                const selectedTableNums = this.selectedTables.map(id => {
                    const table = this.availableTables.find(t => t.id === id);
                    return table ? table.table_number : id;
                }).join(', ');
                tablesInfo = `
                    <div class="tw-flex tw-justify-between tw-py-2 tw-border-t tw-border-gray-200 tw-mt-2">
                        <span class="tw-text-gray-600">Assigned table${this.selectedTables.length > 1 ? 's' : ''}:</span>
                        <span class="tw-font-medium">${selectedTableNums}</span>
                    </div>
                `;
            }
            
            // Build price breakdown
            let priceBreakdown = `
                <div class="tw-flex tw-justify-between tw-py-2">
                    <span>${this.numGuests} adult${this.numGuests > 1 ? 's' : ''} × ${this.settings.currency}${this.settings.pricePerPerson.toFixed(2)}</span>
                    <span>${this.settings.currency}${adultPrice.toFixed(2)}</span>
                </div>
            `;
            
            if (this.numChildren > 0) {
                priceBreakdown += `
                    <div class="tw-flex tw-justify-between tw-py-2">
                        <span>${this.numChildren} child${this.numChildren > 1 ? 'ren' : ''} × ${this.settings.currency}${this.settings.pricePerChild.toFixed(2)}</span>
                        <span>${this.settings.currency}${childPrice.toFixed(2)}</span>
                    </div>
                `;
            }
            
            if (this.numHighChairs > 0) {
                priceBreakdown += `
                    <div class="tw-flex tw-justify-between tw-py-2 tw-text-gray-500">
                        <span>${this.numHighChairs} high chair${this.numHighChairs > 1 ? 's' : ''}</span>
                        <span>Included</span>
                    </div>
                `;
            }
            
            summaryEl.innerHTML = `
                ${priceBreakdown}
                <div class="tw-flex tw-justify-between tw-py-2 tw-border-t tw-border-gray-300 tw-font-semibold">
                    <span>Total</span>
                    <span>${this.settings.currency}${totalPrice.toFixed(2)}</span>
                </div>
                ${tablesInfo}
            `;
        },
        
        // Track if we're in payment step
        paymentFormShown: false,
        
        submitBooking: async function() {
            if (this.isProcessing) return;
            
            const form = this.modal.querySelector('#table-booking-form');
            if (!form) return;
            
            const formData = new FormData(form);
            const totalSeats = this.numGuests + this.numChildren;
            const tablesNeeded = Math.ceil(totalSeats / this.settings.tableCapacity);
            
            // Validate
            if (!formData.get('customer_name') || !formData.get('customer_email')) {
                GDCMFrontend.showNotification('Please fill in all required fields', 'error');
                return;
            }
            
            // Validate table selection - for manual selection check exact count, for auto just ensure tables are selected
            if (this.settings.allowTableSelection) {
                if (this.selectedTables.length !== tablesNeeded) {
                    GDCMFrontend.showNotification(`Please select ${tablesNeeded} table${tablesNeeded > 1 ? 's' : ''}`, 'error');
                    return;
                }
            } else {
                if (this.selectedTables.length === 0) {
                    GDCMFrontend.showNotification('No tables available for your reservation', 'error');
                    return;
                }
            }
            
            // Check if payment is required
            const paymentRequired = gdcmData.tableBooking && gdcmData.tableBooking.paymentRequired;
            const stripeAvailable = window.GDCMStripePayment && window.GDCMStripePayment.stripe;
            
            if (paymentRequired && stripeAvailable) {
                // Check if we're already showing the payment form (second submission)
                if (this.paymentFormShown) {
                    await this.processTablePayment(formData);
                } else {
                    // First submission - show payment form
                    await this.showPaymentForm(formData);
                }
            } else if (paymentRequired && !stripeAvailable) {
                // Payment required but Stripe not available - show error
                GDCMFrontend.showNotification('Payment system is not available. Please contact support.', 'error');
                console.error('Payment required but Stripe not available. Check Stripe configuration.');
            } else {
                // Direct booking without payment
                await this.submitDirectBooking(formData);
            }
        },
        
        showPaymentForm: async function(formData) {
            this.isProcessing = true;
            const submitBtn = this.modal.querySelector('#submit-booking-btn');
            const originalBtnText = submitBtn ? submitBtn.innerHTML : 'Confirm Reservation';
            
            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.innerHTML = '<span class="tw-animate-spin tw-inline-block tw-w-4 tw-h-4 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full tw-mr-2"></span>Loading Payment...';
            }
            
            try {
                const adultPrice = this.numGuests * this.settings.pricePerPerson;
                const childPrice = this.numChildren * this.settings.pricePerChild;
                const totalAmount = adultPrice + childPrice;
                
                // Get or create the payment container in the form
                let paymentContainer = this.modal.querySelector('#gdcm-table-payment-container');
                if (!paymentContainer) {
                    paymentContainer = document.createElement('div');
                    paymentContainer.id = 'gdcm-table-payment-container';
                    
                    // Insert before the submit button
                    const form = this.modal.querySelector('#table-booking-form');
                    const submitBtnContainer = submitBtn.parentElement;
                    form.insertBefore(paymentContainer, submitBtnContainer);
                }
                
                // Insert payment form HTML
                paymentContainer.innerHTML = window.GDCMStripePayment.getPaymentFormHTML(totalAmount);
                
                // Create payment intent and mount element
                const success = await window.GDCMStripePayment.createPaymentIntent({
                    amount: totalAmount,
                    bookingType: 'table_booking',
                    customerEmail: formData.get('customer_email'),
                    customerName: formData.get('customer_name'),
                    num_guests: this.numGuests,
                    num_children: this.numChildren,
                    num_high_chairs: this.numHighChairs,
                    booking_date: this.selectedDate,
                    time_slot: this.selectedTimeSlot,
                    table_ids: this.selectedTables.join(','),
                    containerElement: document.getElementById('gdcm-payment-element')
                });
                
                if (!success) {
                    throw new Error('Failed to initialize payment form');
                }
                
                // Mark that payment form is shown
                this.paymentFormShown = true;
                
                // Update button text
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.innerHTML = 'Pay & Confirm Booking';
                }
                
                // Scroll payment form into view
                paymentContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
                
            } catch (error) {
                GDCMFrontend.showNotification(error.message || 'Failed to initialize payment. Please try again.', 'error');
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.innerHTML = originalBtnText;
                }
            } finally {
                this.isProcessing = false;
            }
        },
        
        processTablePayment: async function(formData) {
            this.isProcessing = true;
            const submitBtn = this.modal.querySelector('#submit-booking-btn');
            
            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.innerHTML = '<span class="tw-animate-spin tw-inline-block tw-w-4 tw-h-4 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full tw-mr-2"></span>Processing Payment...';
            }
            
            try {
                const adultPrice = this.numGuests * this.settings.pricePerPerson;
                const childPrice = this.numChildren * this.settings.pricePerChild;
                const totalAmount = adultPrice + childPrice;
                
                // Prepare booking data for after payment
                const bookingData = {
                    booking_type: 'table_booking',
                    booking_date: this.selectedDate,
                    time_slot: this.selectedTimeSlot,
                    num_guests: this.numGuests,
                    num_children: this.numChildren,
                    num_high_chairs: this.numHighChairs,
                    customer_name: formData.get('customer_name'),
                    customer_email: formData.get('customer_email'),
                    customer_phone: formData.get('customer_phone') || '',
                    observations: formData.get('observations') || '',
                    table_ids: this.selectedTables,
                    total_price: totalAmount
                };
                
                // Process payment using Stripe
                const result = await window.GDCMStripePayment.processPayment(bookingData);
                
                if (result.success) {
                    // Reset payment form
                    window.GDCMStripePayment.reset();
                    this.paymentFormShown = false;
                    
                    // Close modal and show success
                    this.closeModal();
                    this.showSuccessModal(result.bookingId);
                    
                    // Refresh calendar
                    if (this.calendar) {
                        this.calendar.refetchEvents();
                    }
                } else {
                    throw new Error(result.message || 'Payment failed');
                }
                
            } catch (error) {
                window.GDCMStripePayment.showError(error.message || 'Payment failed. Please try again.');
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.innerHTML = 'Pay & Confirm Booking';
                }
            } finally {
                this.isProcessing = false;
            }
        },
        
        submitDirectBooking: async function(formData, paymentIntentId = null) {
            
            this.isProcessing = true;
            const submitBtn = this.modal.querySelector('#submit-booking-btn');
            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.innerHTML = '<span class="tw-animate-spin tw-inline-block tw-w-4 tw-h-4 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full tw-mr-2"></span>Processing...';
            }
            
            try {
                const bookingData = {
                    booking_date: this.selectedDate,
                    time_slot: this.selectedTimeSlot,
                    num_guests: this.numGuests,
                    num_children: this.numChildren,
                    num_high_chairs: this.numHighChairs,
                    customer_name: formData.get('customer_name'),
                    customer_email: formData.get('customer_email'),
                    customer_phone: formData.get('customer_phone') || '',
                    observations: formData.get('observations') || '',
                    table_ids: this.selectedTables
                };
                
                // Add payment info if available
                if (paymentIntentId) {
                    bookingData.payment_intent_id = paymentIntentId;
                    bookingData.payment_status = 'paid';
                }
                
                const response = await fetch(this.buildRestUrl('table-bookings'), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': gdcmData.nonce
                    },
                    body: JSON.stringify(bookingData)
                });
                
                const result = await response.json();
                
                if (result.success) {
                    this.closeModal();
                    this.showSuccessModal(result.booking_id);
                    
                    // Refresh calendar
                    if (this.calendar) {
                        this.calendar.refetchEvents();
                    }
                } else {
                    throw new Error(result.message || 'Booking failed');
                }
            } catch (error) {
                GDCMFrontend.showNotification(error.message || 'Error creating booking', 'error');
            } finally {
                this.isProcessing = false;
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.innerHTML = 'Confirm Reservation';
                }
            }
        },
        
        showSuccessModal: function(bookingId) {
            const successModal = document.getElementById('gdcm-table-booking-success');
            if (successModal) {
                const bookingRef = successModal.querySelector('#booking-reference');
                if (bookingRef) bookingRef.textContent = `#${bookingId}`;
                successModal.classList.remove('tw-hidden');
            }
        },
        
        bindEvents: function() {
            // Close modal buttons
            document.querySelectorAll('.gdcm-table-modal-close').forEach(btn => {
                btn.addEventListener('click', () => this.closeModal());
            });
            
            // Click outside modal to close
            document.querySelectorAll('.gdcm-table-modal-overlay').forEach(overlay => {
                overlay.addEventListener('click', (e) => {
                    if (e.target === overlay) this.closeModal();
                });
            });
            
            // Guest count buttons
            document.querySelector('#decrease-guests')?.addEventListener('click', () => this.updateGuestCount(-1));
            document.querySelector('#increase-guests')?.addEventListener('click', () => this.updateGuestCount(1));
            
            // Children count buttons
            document.querySelector('#decrease-children')?.addEventListener('click', () => this.updateChildrenCount(-1));
            document.querySelector('#increase-children')?.addEventListener('click', () => this.updateChildrenCount(1));
            
            // High chairs count buttons
            document.querySelector('#decrease-high-chairs')?.addEventListener('click', () => this.updateHighChairsCount(-1));
            document.querySelector('#increase-high-chairs')?.addEventListener('click', () => this.updateHighChairsCount(1));
            
            // Step navigation
            document.querySelector('#step1-next')?.addEventListener('click', () => {
                if (this.selectedTimeSlot) this.showStep(2);
            });
            
            document.querySelector('#step2-back')?.addEventListener('click', () => this.showStep(1));
            document.querySelector('#step2-next')?.addEventListener('click', async () => {
                if (this.settings.allowTableSelection) {
                    // Manual table selection - go to step 3
                    this.showStep(3);
                    this.loadAvailableTables();
                } else {
                    // Automatic table selection - skip step 3
                    await this.loadAvailableTablesForAutoSelect();
                    if (this.autoSelectTables()) {
                        this.showStep(4);
                        this.updatePriceSummary();
                    }
                }
            });
            
            document.querySelector('#step3-back')?.addEventListener('click', () => this.showStep(2));
            document.querySelector('#step3-next')?.addEventListener('click', () => {
                if (this.selectedTables.length > 0) {
                    this.showStep(4);
                    this.updatePriceSummary();
                }
            });
            
            // Step 4 back button - go to step 3 if manual selection, otherwise step 2
            document.querySelector('#step4-back')?.addEventListener('click', () => {
                if (this.settings.allowTableSelection) {
                    this.showStep(3);
                } else {
                    this.showStep(2);
                }
            });
            
            // Form submission
            document.querySelector('#table-booking-form')?.addEventListener('submit', (e) => {
                e.preventDefault();
                this.submitBooking();
            });
            
            // Success modal close
            document.querySelector('#close-success-modal')?.addEventListener('click', () => {
                const successModal = document.getElementById('gdcm-table-booking-success');
                if (successModal) successModal.classList.add('tw-hidden');
            });
            
            // Escape key
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') {
                    this.closeModal();
                    const successModal = document.getElementById('gdcm-table-booking-success');
                    if (successModal) successModal.classList.add('tw-hidden');
                }
            });
        }
    };
    
    // Initialize table booking if element exists
    GDCMTableBooking.init();
    
    // Make globally available
    window.GDCMTableBooking = GDCMTableBooking;
} // End initFrontend

})(); // End IIFE
